/**
 * Вставка файлов из буфера обмена (Ctrl+V / ⌘V / «Вставить») в поле ответа
 * переписки по помещению (владелец 19.10.2026: «скопировал фото или сделал скриншот
 * и не могу прикрепить из буфера»). Чистые функции без React — покрыто
 * `clipboard-files.test.ts`.
 *
 * Копия есть в CRM: `frontend/src/lib/clipboard-files.ts` (разные пакеты, общего
 * места нет) — правила держать одинаковыми.
 */

/** Минимум от `DataTransfer`, нужный для разбора вставки (в тестах — обычные объекты). */
export interface ClipboardLike {
  files?: ArrayLike<File> | null;
  items?: ArrayLike<{ kind: string; type: string; getAsFile(): File | null }> | null;
  getData?(format: string): string;
}

export interface PastedFiles {
  /** Файлы из буфера (скриншот, скопированный фото-файл, картинка из браузера). Пусто — вставляется обычный текст. */
  files: File[];
  /**
   * true — стандартную вставку браузера отменяем (`preventDefault`): в поле нечего
   * вставлять, кроме файла. false — вместе с файлом в буфере настоящий текст
   * (копирование из Excel/Word): текст не глотаем, пусть вставится как всегда.
   */
  swallowDefault: boolean;
}

const NOTHING: PastedFiles = { files: [], swallowDefault: false };

function readText(data: ClipboardLike): string {
  try {
    return data.getData?.('text/plain') ?? '';
  } catch {
    return '';
  }
}

/**
 * Файлы из `clipboardData`. Chrome/Firefox отдают скриншот и в `files`, и в `items`
 * (kind «file») — берём `files`, а в `items` заглядываем, только когда там пусто
 * (Safari, часть Linux-сборок): иначе один скриншот попал бы дважды.
 * Текстовые элементы (kind «string») и `getAsFile() === null` пропускаем.
 */
export function pickPastedFiles(data: ClipboardLike | null | undefined): PastedFiles {
  if (!data) return NOTHING;
  let files = Array.from(data.files ?? []).filter((f): f is File => !!f);
  if (!files.length) {
    files = Array.from(data.items ?? [])
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter((f): f is File => !!f);
  }
  if (!files.length) return NOTHING;

  const text = readText(data).trim();
  if (!text) return { files, swallowDefault: true };
  // Finder на Mac кладёт вместе с файлом его имя текстом — это не «настоящий» текст,
  // вставлять имя в поле ответа не нужно.
  const names = new Set(files.map((f) => f.name.trim()));
  const onlyFileNames = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .every((l) => names.has(l));
  // Настоящий текст рядом с картинкой — это копирование из Excel/Word (они кладут ещё и картинку
  // диапазона): вставляем только текст, лишнюю картинку не прикрепляем.
  return onlyFileNames ? { files, swallowDefault: true } : NOTHING;
}

// ───────── Ctrl+V на Mac: буфер через Clipboard API ─────────

/** Минимум от `navigator.clipboard` (в тестах — обычный объект). */
export interface AsyncClipboardLike {
  read?(): Promise<ArrayLike<{ types: readonly string[]; getType(type: string): Promise<Blob> }>>;
  readText?(): Promise<string>;
}

/**
 * Содержимое буфера для вставки, когда браузер сам не вставил (Ctrl+V на Mac — там вставка ⌘V,
 * владелец 30.10.2026). Правила как у `pickPastedFiles`: есть настоящий текст — вставляем текст,
 * картинку рядом (Excel/Word кладут её вместе с текстом) не прикрепляем; текста нет — берём
 * картинки. Без `read` (старые браузеры) — только текст. Ошибку доступа к буферу не глотаем:
 * вызывающий скажет, как вставить штатно.
 */
export async function readClipboardForPaste(cb: AsyncClipboardLike | null | undefined): Promise<{ text: string; files: File[] }> {
  if (!cb) return { text: '', files: [] };
  if (!cb.read) return { text: cb.readText ? await cb.readText() : '', files: [] };
  let text = '';
  const files: File[] = [];
  for (const it of Array.from(await cb.read())) {
    if (it.types.includes('text/plain')) {
      text += await (await it.getType('text/plain')).text();
      continue;
    }
    const type = it.types.find((t) => t.startsWith('image/'));
    if (type) files.push(new File([await it.getType(type)], '', { type }));
  }
  return text.trim() ? { text, files: [] } : { text: '', files };
}

// ───────── Имена вставленных файлов ─────────

/** Скриншот из буфера приходит без имени или как `image.png` / `image (1).png` / `blob`. */
const GENERIC_IMAGE_NAME = /^(image|blob)(\s*\(\d+\))?(\.[a-z0-9]+)?$/i;

const EXT_BY_MIME: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/avif': 'avif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/tiff': 'tiff',
  'image/svg+xml': 'svg',
  'application/pdf': 'pdf',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};

const pad = (n: number): string => String(n).padStart(2, '0');

/** «2026-10-19 17-26» — локальное время браузера, без двоеточий (в имени файла не нужны). */
function stamp(now: Date): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}-${pad(now.getMinutes())}`;
}

function extOf(name: string): string {
  const m = /\.([a-z0-9]{1,8})$/i.exec(name.trim());
  return m ? m[1]!.toLowerCase() : '';
}

/** Нужно ли дать файлу из буфера своё имя: пустое или «служебное» имя картинки. */
export function needsPastedName(file: Pick<File, 'name' | 'type'>): boolean {
  const name = file.name.trim();
  if (!name) return true;
  return file.type.toLowerCase().startsWith('image/') && GENERIC_IMAGE_NAME.test(name);
}

/** «Скриншот 2026-10-19 17-26.png» (картинка) / «Файл из буфера 2026-10-19 17-26.pdf» (прочее без имени). */
export function pastedFileName(file: Pick<File, 'name' | 'type'>, now: Date): string {
  const mime = file.type.toLowerCase();
  const isImage = mime.startsWith('image/');
  const sub = /^image\/([a-z0-9]+)$/.exec(mime)?.[1];
  const ext = EXT_BY_MIME[mime] || extOf(file.name) || sub || (isImage ? 'png' : '');
  return `${isImage ? 'Скриншот' : 'Файл из буфера'} ${stamp(now)}${ext ? `.${ext}` : ''}`;
}

/** «Скриншот … .png» → «Скриншот … (2).png», если такое имя уже занято. */
function uniqueName(name: string, used: Set<string>): string {
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  let candidate = name;
  for (let n = 2; used.has(candidate.toLowerCase()); n += 1) candidate = `${stem} (${n})${ext}`;
  return candidate;
}

/**
 * Даёт понятные имена файлам из буфера. Файлы с настоящим именем (скопирован фото-файл
 * в проводнике / Finder) не трогаем; безымянные и `image.png` получают «Скриншот <дата время>.png».
 * Несколько вставок в одну минуту не совпадают по имени: к повторам добавляется « (2)», « (3)» —
 * с учётом уже прикреплённых файлов (`taken`).
 */
export function nameClipboardFiles(files: File[], taken: string[], now: Date = new Date()): File[] {
  const used = new Set(taken.map((n) => n.trim().toLowerCase()));
  return files.map((f) => {
    if (!needsPastedName(f)) {
      used.add(f.name.trim().toLowerCase());
      return f;
    }
    const name = uniqueName(pastedFileName(f, now), used);
    used.add(name.toLowerCase());
    return new File([f], name, { type: f.type, lastModified: f.lastModified });
  });
}
